<script lang="ts" setup generic="TObj extends BaseObj">
/**
 * This component wraps the input component within the DetailComp.
 * So each one of the form input control is wrapped within this component.
 */
import type { BaseObj } from '@/objects/BaseObj';
import type { FieldDef } from '@/objects/gui/FieldDef';
import { FieldAlertMessage } from '@/objects';

import { ref, computed } from 'vue';
import type { IEditorComponent } from '@/objects/gui';

const props = defineProps<{
    field: FieldDef<TObj>;
    modelValue: TObj;
    id: string;
}>();

const emit = defineEmits(['update:modelValue']);

const value = computed({
    get: () => props.field.value(props.modelValue),
    set: (value) => {
        props.field.setValue(props.modelValue, value);
        emit('update:modelValue', props.modelValue);
    },
});

const component = ref<IEditorComponent | null>(null);

/**
 * Listens for the message from the component, and then transforms it to a FieldAlertMessage
 */
const message = computed(() => {
    if (!component.value) {
        return null;
    }

    return new FieldAlertMessage(
        props.field.Name,
        component.value.validateMessage?.message,
        component.value.validateMessage?.type,
        props.field.TranslationKey,
    );
});

defineExpose({ message });

const detailEditorProps =
    props.field.DetailEditorPropsGetter !== undefined
        ? props.field.DetailEditorPropsGetter(props.modelValue)
        : props.field.DetailEditorProps;
</script>

<template>
    <component
        :is="field.DetailEditor"
        :field="field"
        :id="id"
        :validator="field.Validator"
        :row="props.modelValue"
        :editable="field.Editable"
        v-model="value"
        v-bind="detailEditorProps"
        ref="component"
    ></component>
</template>
